import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SchoolIcon from '@mui/icons-material/School';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import GppGoodIcon from '@mui/icons-material/GppGood';
import PolicyIcon from '@mui/icons-material/Policy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { Link } from 'react-router-dom';

const Iso42001: React.FC = () => {
  const implementationProcess = [
    {
      title: "AI Governance Gap Analysis",
      description: "We assess your current AI governance, controls, and practices against ISO 42001 requirements to identify gaps and priorities.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Scope & AIMS Planning",
      description: "We define the scope of your AI Management System (AIMS), set objectives, and establish roles and accountability.",
      icon: <AccountTreeIcon fontSize="large" />
    },
    {
      title: "AI Risk Management",
      description: "We help you identify, assess, and treat AI risks across the AI lifecycle, including model, data, and operational risks.",
      icon: <PolicyIcon fontSize="large" />
    },
    {
      title: "Controls & Operationalization",
      description: "We implement practical controls for responsible AI including oversight, human-in-the-loop, monitoring, and change management.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We develop the required policies, procedures, and records for ISO 42001 compliance and audit readiness.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Training & Awareness",
      description: "We train stakeholders on responsible AI practices, governance responsibilities, and the day-to-day operation of the AIMS.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Internal Audit",
      description: "We conduct internal audits and readiness checks to validate AIMS effectiveness before certification.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Certification Support",
      description: "We support you through the certification audit and help close any nonconformities efficiently.",
      icon: <GppGoodIcon fontSize="large" />
    }
  ];

  const coreFocusAreas = [
    {
      title: "AI Governance & Accountability",
      description: "Define roles, responsibilities, oversight structures, and decision-making for AI initiatives.",
      icon: <PolicyIcon fontSize="large" />
    },
    {
      title: "Lifecycle Risk Management",
      description: "Risk identification, assessment, treatment, and monitoring across design, development, deployment, and retirement.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Operational Controls",
      description: "Controls for monitoring, change management, incident handling, and continuous improvement of AI systems.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Documentation & Evidence",
      description: "Maintain policies, procedures, and records that demonstrate responsible AI management and compliance.",
      icon: <FactCheckIcon fontSize="large" />
    }
  ];

  const benefits = [
    "Establishes a structured AI management system for responsible AI governance",
    "Improves AI risk management and reduces the likelihood of harmful outcomes",
    "Demonstrates trustworthy AI practices to customers and stakeholders",
    "Enhances audit readiness and alignment with emerging AI regulations",
    "Strengthens oversight across AI lifecycle changes and operational monitoring",
    "Supports integration with other management systems (e.g., ISO 9001, ISO 27001)",
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              ISO 42001:2023 AI Management System
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Implementation and certification support for responsible AI governance and AI risk management
            </Typography>
          </motion.div>
        </Container>

        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                fontWeight="bold"
                color="#0c2340"
              >
                What is ISO 42001:2023?
              </Typography>
              <Typography paragraph>
                ISO 42001:2023 is the international standard for an AI Management System (AIMS).
                It provides a management-system framework to govern AI responsibly, establish accountability,
                and manage AI risks across the AI lifecycle.
              </Typography>
              <Typography paragraph>
                ISO 42001 helps organizations create repeatable controls for AI oversight, monitoring,
                incident handling, and continual improvement—supporting trustworthy AI in products,
                services, and internal operations.
              </Typography>
              <Typography paragraph>
                Certification demonstrates to customers, partners, and stakeholders that your organization
                has implemented a structured approach to responsible AI governance and AI risk management.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1470&q=80"
                alt="AI Governance"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Core Focus Areas */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              Core Focus Areas
            </Typography>
          </motion.div>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 4
          }}>
            {coreFocusAreas.map((area, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      bgcolor: '#1976d2',
                    }} />
                    <Box sx={{
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#1976d215',
                      color: '#1976d2',
                      mb: 3,
                    }}>
                      {area.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {area.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {area.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Our ISO 42001 Implementation Process
          </Typography>
          <Typography
            variant="body1"
            paragraph
            align="center"
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            A practical, structured approach to implement ISO 42001:2023 and prepare for certification.
          </Typography>
        </motion.div>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 4
        }}>
          {implementationProcess.map((step, index) => (
            <Box key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#1976d215',
                      color: '#1976d2',
                      mr: 2
                    }}>
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Benefits of ISO 42001 Certification
          </Typography>
        </motion.div>

        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f9f9f9 0%, #f5f7fa 100%)'
          }}
        >
          <List>
            {benefits.map((benefit, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={benefit} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1e3c72 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, opacity: 0.95 }}>
                <SmartToyIcon sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Govern AI Responsibly?
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  maxWidth: '700px',
                  mx: 'auto',
                  mb: 4,
                  opacity: 0.9
                }}
              >
                Our ISO 42001 experts can help you establish an AI management system, operationalize controls,
                and prepare for certification. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=iso42001"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services/iso"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  View All ISO Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Iso42001;

